import { computed, reactive } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'

import {
  type FavoritesToastState,
  ToastName,
  defaultState,
} from './FavoritesToast'
import translations from './FavoritesToast.translations'

function createSavedFavToastDefaults(
  i18n: ReturnType<typeof useI18n>,
): FavoritesToastState {
  return {
    ...defaultState,
    title: i18n(translations.savedFavTitle),
    content: i18n(translations.savedFavContent),
    actionText: i18n(translations.savedFavActionText),
    closeAlternativeText: i18n(translations.closeToast),
  }
}

function createAlreadySavedFavToastDefaults(
  i18n: ReturnType<typeof useI18n>,
): FavoritesToastState {
  return {
    ...defaultState,
    title: i18n(translations.alreadySavedFavTitle),
    content: i18n(translations.alreadySavedFavContent),
    actionText: i18n(translations.alreadySavedFavActionText),
    closeAlternativeText: i18n(translations.closeToast),
    name: ToastName.AlreadySavedFavorite,
  }
}

function createRemoveFavToastDefaults(
  i18n: ReturnType<typeof useI18n>,
): FavoritesToastState {
  return {
    ...defaultState,
    title: i18n(translations.removeFavTitle),
    content: i18n(translations.removeFavContent),
    actionText: i18n(translations.removeFavActionText),
    closeAlternativeText: i18n(translations.closeToast),
    name: ToastName.RemoveFavorite,
    type: 'error',
  }
}

export interface FavoritesToastInterface {
  toastInfo: FavoritesToastState
  openSavedFavToast(): void
  openAlreadySavedFavToast(): void
  openRemoveFavToast(): void
  close(): void
}

export function useFavoritesToast() {
  const toastInfo: FavoritesToastState = reactive({ ...defaultState })
  const i18n = useI18n()

  const isRemoveFavoriteToastOpen = computed(() => {
    const { isOpen: toastIsOpen, name: toastName } = toastInfo
    const toastIsRemoveFavoriteType = toastName === ToastName.RemoveFavorite

    return toastIsOpen && toastIsRemoveFavoriteType
  })

  const favoritesToast: FavoritesToastInterface = {
    toastInfo,

    openSavedFavToast() {
      Object.assign(toastInfo, createSavedFavToastDefaults(i18n))
      toastInfo.isOpen = true
    },

    openAlreadySavedFavToast() {
      Object.assign(toastInfo, createAlreadySavedFavToastDefaults(i18n))
      toastInfo.isOpen = true
    },

    openRemoveFavToast() {
      Object.assign(toastInfo, createRemoveFavToastDefaults(i18n))
      toastInfo.isOpen = true
    },

    close() {
      Object.assign(toastInfo, defaultState)
    },
  }

  return {
    favoritesToast,
    isRemoveFavoriteToastOpen,
  }
}
